import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getFurnitureImageUrl,
  listFurnitureMoodboards, listFurnitureMoodboardsViaShare, listFurnitureMoodboardsViaView,
  uploadFurnitureMoodboard, uploadFurnitureMoodboardViaShare,
  deleteFurnitureMoodboard, deleteFurnitureMoodboardViaShare,
  reorderFurnitureMoodboards, reorderFurnitureMoodboardsViaShare,
  listFurnitureFloorplans, listFurnitureFloorplansViaShare, listFurnitureFloorplansViaView,
  uploadFurnitureFloorplan, uploadFurnitureFloorplanViaShare,
  deleteFurnitureFloorplan, deleteFurnitureFloorplanViaShare,
  reorderFurnitureFloorplans, reorderFurnitureFloorplansViaShare,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Upload, Trash2, Combine, GripVertical, Loader2, X, LayoutGrid, Rows, ImageIcon, Map } from 'lucide-react';
import { ConfirmDeleteDialog } from '@/components/furniture/ConfirmDeleteDialog';

// Two parallel "image gallery" collections per room: mood boards and
// floor plans. The page is the same in both modes — only the API
// endpoints, copy, and the active tab change.
const KIND_CONFIG = {
  moodboards: {
    label: 'Mood Boards',
    singular: 'Mood Board',
    routeSuffix: 'mood-board',
    apis: {
      list: listFurnitureMoodboards,
      listShare: listFurnitureMoodboardsViaShare,
      listView: listFurnitureMoodboardsViaView,
      upload: uploadFurnitureMoodboard,
      uploadShare: uploadFurnitureMoodboardViaShare,
      del: deleteFurnitureMoodboard,
      delShare: deleteFurnitureMoodboardViaShare,
      reorder: reorderFurnitureMoodboards,
      reorderShare: reorderFurnitureMoodboardsViaShare,
    },
  },
  floorplans: {
    label: 'Floor Plans',
    singular: 'Floor Plan',
    routeSuffix: 'floor-plans',
    apis: {
      list: listFurnitureFloorplans,
      listShare: listFurnitureFloorplansViaShare,
      listView: listFurnitureFloorplansViaView,
      upload: uploadFurnitureFloorplan,
      uploadShare: uploadFurnitureFloorplanViaShare,
      del: deleteFurnitureFloorplan,
      delShare: deleteFurnitureFloorplanViaShare,
      reorder: reorderFurnitureFloorplans,
      reorderShare: reorderFurnitureFloorplansViaShare,
    },
  },
};

/**
 * Room "Mood Board" — now a viewer/gallery rather than a canvas editor.
 *
 *  ── Sidebar (left) ──
 *    Lists every mood board the room has. Click to view in the center.
 *    Drag the handle to reorder. Trash icon to delete. Checkbox to select
 *    up to 2 boards for combining.
 *
 *  ── Center ──
 *    The selected board fills the area. If no boards exist yet, a big
 *    upload prompt is shown instead. There's also a top toolbar with the
 *    "Upload Mood Board" button (always visible) and the "Combine" CTA
 *    that lights up once 2 boards are selected.
 *
 *  ── Combine modal ──
 *    Composites two boards into a single PNG on a <canvas> client-side
 *    (no server work), with a side-by-side / stacked toggle, then uploads
 *    the result as a new mood board.
 */

const ASPECT_HOR = 'horizontal';
const ASPECT_VER = 'vertical';

export default function RoomMoodBoardPage({ kind = 'moodboards' }) {
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.moodboards;
  const { list, listShare, listView, upload, uploadShare, del, delShare, reorder, reorderShare } = cfg.apis;
  const { projectId, roomId, shareToken, viewToken } = useParams();
  const navigate = useNavigate();
  const isViewMode = !!viewToken;
  const readOnly = isViewMode;

  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [selectedForCombine, setSelectedForCombine] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [combineOpen, setCombineOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const fileInputRef = useRef(null);

  // ── Data loading ────────────────────────────────────────────
  const fetchBoards = useCallback(async () => {
    try {
      let result;
      if (shareToken) result = await listShare(shareToken, roomId);
      else if (viewToken) result = await listView(viewToken, roomId);
      else result = await list(projectId, roomId);
      setBoards(result || []);
      setSelectedViewId(prev => {
        if (prev && result?.some(b => b.id === prev)) return prev;
        return result?.[0]?.id || null;
      });
    } catch { toast.error(`Failed to load ${cfg.label.toLowerCase()}`); }
    setLoading(false);
  }, [projectId, roomId, shareToken, viewToken, list, listShare, listView, cfg.label]);

  useEffect(() => { fetchBoards(); }, [fetchBoards]);

  // ── Upload ──────────────────────────────────────────────────
  const handleUpload = async (file, { combinedFrom, name } = {}) => {
    if (!file) return;
    setUploading(true);
    try {
      let newBoard;
      if (shareToken) newBoard = await uploadShare(shareToken, roomId, file, { combinedFrom, name });
      else newBoard = await upload(projectId, roomId, file, { combinedFrom, name });
      setBoards(prev => [...prev, newBoard]);
      setSelectedViewId(newBoard.id);
      toast.success(`${cfg.singular} uploaded`);
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) handleUpload(f);
  };

  // ── Delete ──────────────────────────────────────────────────
  const doDelete = async (id) => {
    // Optimistic remove — fall back to a refetch if the network call fails.
    const prev = boards;
    setBoards(b => b.filter(x => x.id !== id));
    setSelectedForCombine(s => { const n = new Set(s); n.delete(id); return n; });
    if (selectedViewId === id) setSelectedViewId(prev.find(b => b.id !== id)?.id || null);
    try {
      if (shareToken) await delShare(shareToken, roomId, id);
      else await del(projectId, roomId, id);
      toast.success(`${cfg.singular} deleted`);
    } catch { toast.error('Delete failed'); setBoards(prev); }
  };

  // ── Reorder (drag-and-drop with native HTML5 DnD; light-weight) ─
  const [dragId, setDragId] = useState(null);
  const onDragStart = (id) => (e) => {
    if (readOnly) return;
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = boards.map(b => b.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const next = [...boards];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setBoards(next);
    setDragId(null);
    const orderedIds = next.map(b => b.id);
    // Fire-and-forget; on failure we still keep optimistic order but warn.
    const call = shareToken
      ? reorderShare(shareToken, roomId, orderedIds)
      : reorder(projectId, roomId, orderedIds);
    call.catch(() => toast.error('Reorder failed; refresh to revert.'));
  };

  // ── Combine selection helpers ───────────────────────────────
  const toggleSelect = (id) => {
    setSelectedForCombine(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= 2) {
          // Replace the oldest selection so user can swap without un-checking first.
          const first = next.values().next().value;
          next.delete(first);
        }
        next.add(id);
      }
      return next;
    });
  };

  const combineBoards = useMemo(() =>
    boards.filter(b => selectedForCombine.has(b.id)),
    [boards, selectedForCombine]);

  // ── Nav ─────────────────────────────────────────────────────
  const goBack = () => {
    if (shareToken) navigate(`/furniture-projects/share/${shareToken}`);
    else if (viewToken) navigate(`/furniture-projects/view/${viewToken}`);
    else if (typeof window !== 'undefined' && window.location.pathname.startsWith('/staff/')) {
      navigate(`/staff/projects/${projectId}`);
    }
    else navigate(`/furniture-projects/${projectId}`);
  };

  // Sibling URL for the OTHER kind so the tab toggle can navigate.
  const otherKind = kind === 'moodboards' ? 'floorplans' : 'moodboards';
  const otherSuffix = KIND_CONFIG[otherKind].routeSuffix;
  const switchKindUrl = (() => {
    if (shareToken) return `/furniture-projects/share/${shareToken}/rooms/${roomId}/${otherSuffix}`;
    if (viewToken) return `/furniture-projects/view/${viewToken}/rooms/${roomId}/${otherSuffix}`;
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/staff/')) {
      return `/staff/projects/${projectId}/rooms/${roomId}/${otherSuffix}`;
    }
    return `/furniture-projects/${projectId}/rooms/${roomId}/${otherSuffix}`;
  })();

  if (loading) return (
    <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center">
      <div className="animate-pulse text-[#5b6773]">Loading…</div>
    </div>
  );

  const selected = boards.find(b => b.id === selectedViewId) || null;

  return (
    <div className="h-screen bg-[#fbfaf7] flex flex-col overflow-hidden">
      {/* ── Top toolbar ─────────────────────────────── */}
      <header className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#e7e2d4] bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={goBack} className="h-8 w-8" data-testid="mb-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-base font-heading font-semibold text-[#1f2933] truncate">{cfg.label}</h1>
          <span className="text-xs text-[#5b6773]">{boards.length} {boards.length === 1 ? cfg.singular.toLowerCase() : cfg.label.toLowerCase()}</span>

          {/* Tabs: Mood Boards ↔ Floor Plans */}
          <div className="ml-2 inline-flex items-center bg-[#f1ede4] rounded-full p-0.5 text-xs" data-testid="room-gallery-tabs">
            <button
              type="button"
              onClick={() => kind !== 'moodboards' && navigate(switchKindUrl)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${kind === 'moodboards' ? 'bg-white shadow text-[#1f2933]' : 'text-[#5b6773] hover:text-[#1f2933]'}`}
              data-testid="tab-moodboards"
            >
              <ImageIcon className="h-3 w-3" /> Mood Boards
            </button>
            <button
              type="button"
              onClick={() => kind !== 'floorplans' && navigate(switchKindUrl)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${kind === 'floorplans' ? 'bg-white shadow text-[#1f2933]' : 'text-[#5b6773] hover:text-[#1f2933]'}`}
              data-testid="tab-floorplans"
            >
              <Map className="h-3 w-3" /> Floor Plans
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!readOnly && selectedForCombine.size === 2 && (
            <Button
              size="sm"
              onClick={() => setCombineOpen(true)}
              className="gap-1.5 bg-[#a8324a] hover:bg-[#8e2839]"
              data-testid="mb-open-combine"
            >
              <Combine className="h-4 w-4" /> Combine 2
            </Button>
          )}
          {!readOnly && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickFile}
                data-testid="mb-file-input"
              />
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="gap-1.5"
                data-testid="mb-upload"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload {cfg.singular}
              </Button>
            </>
          )}
        </div>
      </header>

      {/* ── Sidebar + Center ─────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        <aside className="w-[220px] border-r border-[#e7e2d4] bg-white flex flex-col">
          <p className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-[#5b6773]/70 border-b border-[#e7e2d4]/60">
            Boards
          </p>
          <div className="flex-1 overflow-y-auto py-2">
            {boards.length === 0 && (
              <p className="px-3 py-4 text-xs text-[#5b6773]/60 italic">No boards yet — upload one to get started.</p>
            )}
            {boards.map((b) => {
              const isSelected = selectedViewId === b.id;
              const isChecked = selectedForCombine.has(b.id);
              return (
                <div
                  key={b.id}
                  draggable={!readOnly}
                  onDragStart={onDragStart(b.id)}
                  onDragOver={onDragOver}
                  onDrop={onDrop(b.id)}
                  onClick={() => setSelectedViewId(b.id)}
                  className={`group mx-2 mb-2 rounded-lg overflow-hidden border cursor-pointer transition-all ${
                    isSelected ? 'border-[#a8324a] ring-2 ring-[#a8324a]/30' : 'border-[#e7e2d4] hover:border-[#5b6773]/40'
                  } ${dragId === b.id ? 'opacity-40' : ''}`}
                  data-testid={`mb-sidebar-${b.id}`}
                >
                  <div className="relative aspect-video bg-[#f1ede4]">
                    <img
                      src={getFurnitureImageUrl(b.url)}
                      alt={b.name || 'Mood board'}
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                    {!readOnly && (
                      <>
                        <label
                          className="absolute top-1 left-1 flex items-center justify-center w-5 h-5 rounded bg-white/95 border border-[#e7e2d4] cursor-pointer shadow-sm hover:bg-white"
                          onClick={(e) => { e.stopPropagation(); }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelect(b.id)}
                            className="cursor-pointer"
                            data-testid={`mb-select-${b.id}`}
                          />
                        </label>
                        <button
                          onClick={(e) => { e.stopPropagation(); setPendingDelete(b); }}
                          className="absolute top-1 right-1 p-1 rounded bg-white/95 border border-[#e7e2d4] text-[#a8324a]/80 hover:text-[#a8324a] hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Delete mood board"
                          data-testid={`mb-delete-${b.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                        <span
                          className="absolute bottom-1 right-1 p-0.5 rounded bg-white/95 border border-[#e7e2d4] text-[#5b6773] cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Drag to reorder"
                          title="Drag to reorder"
                        >
                          <GripVertical className="h-3 w-3" />
                        </span>
                      </>
                    )}
                    {b.combined_from && (
                      <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-wider font-semibold bg-[#a8324a]/90 text-white px-1.5 py-0.5 rounded">
                        Combined
                      </span>
                    )}
                  </div>
                  {b.name && (
                    <p className="px-2 py-1 text-[11px] text-[#1f2933] truncate" title={b.name}>{b.name}</p>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* ── Center ──────────────────────────────────── */}
        <main className="flex-1 overflow-auto bg-[#f1ede4] relative">
          {boards.length === 0 ? (
            <EmptyState readOnly={readOnly} onUploadClick={() => fileInputRef.current?.click()} uploading={uploading} />
          ) : selected ? (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <img
                src={getFurnitureImageUrl(selected.url)}
                alt={selected.name || 'Mood board'}
                className="max-w-full max-h-full object-contain rounded-lg shadow-[0_8px_32px_rgba(16,24,40,0.18)]"
                data-testid="mb-main-image"
              />
            </div>
          ) : null}
        </main>
      </div>

      {/* ── Combine dialog ──────────────────────────── */}
      {combineOpen && combineBoards.length === 2 && (
        <CombineDialog
          boards={combineBoards}
          onClose={() => setCombineOpen(false)}
          onSave={async (file, name) => {
            await handleUpload(file, {
              combinedFrom: combineBoards.map(b => b.id),
              name,
            });
            setSelectedForCombine(new Set());
            setCombineOpen(false);
          }}
        />
      )}

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={`Delete "${pendingDelete?.name || 'this mood board'}"?`}
        description="The image will be removed from this room's mood boards. This cannot be undone."
        destructiveLabel="Delete mood board"
        onConfirm={() => { if (pendingDelete) doDelete(pendingDelete.id); setPendingDelete(null); }}
      />
    </div>
  );
}

// ── Empty state shown when the room has zero mood boards ──────
function EmptyState({ readOnly, onUploadClick, uploading }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-white border border-[#e7e2d4] flex items-center justify-center mb-4 shadow-sm">
          <Upload className="h-7 w-7 text-[#5b6773]/60" />
        </div>
        <h2 className="text-xl font-heading font-semibold text-[#1f2933] mb-1">No mood boards yet</h2>
        <p className="text-sm text-[#5b6773] mb-5">
          {readOnly
            ? 'Mood boards uploaded by the design team will appear here.'
            : 'Drop in an inspiration image — interior shots, designer mockups, palettes, anything that captures the room\'s direction.'}
        </p>
        {!readOnly && (
          <Button onClick={onUploadClick} disabled={uploading} className="gap-1.5" data-testid="mb-empty-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload Mood Board
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Combine 2 boards into a single composite ──────────────────
function CombineDialog({ boards, onClose, onSave }) {
  const [layout, setLayout] = useState(ASPECT_HOR); // horizontal | vertical
  const [name, setName] = useState('Combined Board');
  const [saving, setSaving] = useState(false);
  const previewCanvasRef = useRef(null);

  // Pre-load both images so the canvas preview / composite work.
  const [imgs, setImgs] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const out = await Promise.all(boards.map(b => new Promise((res, rej) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = getFurnitureImageUrl(b.url);
      }))).catch(() => null);
      if (!cancelled) {
        if (!out) setLoadErr(true);
        else setImgs(out);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [boards]);

  // Compute composite dimensions: align on the shared axis.
  const composite = useMemo(() => {
    if (!imgs) return null;
    const [a, b] = imgs;
    if (layout === ASPECT_HOR) {
      // Match heights; scale widths proportionally.
      const H = Math.max(a.naturalHeight, b.naturalHeight);
      const aW = Math.round(a.naturalWidth * (H / a.naturalHeight));
      const bW = Math.round(b.naturalWidth * (H / b.naturalHeight));
      return { width: aW + bW, height: H, parts: [
        { img: a, x: 0, y: 0, w: aW, h: H },
        { img: b, x: aW, y: 0, w: bW, h: H },
      ] };
    }
    // Vertical: match widths; scale heights.
    const W = Math.max(a.naturalWidth, b.naturalWidth);
    const aH = Math.round(a.naturalHeight * (W / a.naturalWidth));
    const bH = Math.round(b.naturalHeight * (W / b.naturalWidth));
    return { width: W, height: aH + bH, parts: [
      { img: a, x: 0, y: 0, w: W, h: aH },
      { img: b, x: 0, y: aH, w: W, h: bH },
    ] };
  }, [imgs, layout]);

  // Render the preview onto a canvas (also reused for the final upload).
  useEffect(() => {
    if (!composite) return;
    const cv = previewCanvasRef.current;
    if (!cv) return;
    cv.width = composite.width;
    cv.height = composite.height;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cv.width, cv.height);
    for (const p of composite.parts) {
      ctx.drawImage(p.img, p.x, p.y, p.w, p.h);
    }
  }, [composite]);

  const handleSave = async () => {
    const cv = previewCanvasRef.current;
    if (!cv) return;
    setSaving(true);
    cv.toBlob(async (blob) => {
      if (!blob) { setSaving(false); toast.error('Could not generate image'); return; }
      const file = new File([blob], `combined-${Date.now()}.png`, { type: 'image/png' });
      try {
        await onSave(file, name.trim() || 'Combined Board');
      } finally {
        setSaving(false);
      }
    }, 'image/png', 0.92);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="mb-combine-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e7e2d4]">
          <div>
            <h2 className="font-heading font-semibold text-[#1f2933]">Combine 2 mood boards</h2>
            <p className="text-xs text-[#5b6773]">Preview the composite, then save it as a new mood board.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f1ede4]" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#e7e2d4]/60 bg-[#fbfaf7]">
          <span className="text-xs text-[#5b6773] mr-2">Layout:</span>
          <Button
            variant={layout === ASPECT_HOR ? 'default' : 'outline'}
            size="sm" className="gap-1.5"
            onClick={() => setLayout(ASPECT_HOR)}
            data-testid="mb-combine-horizontal"
          >
            <LayoutGrid className="h-4 w-4" /> Side-by-side
          </Button>
          <Button
            variant={layout === ASPECT_VER ? 'default' : 'outline'}
            size="sm" className="gap-1.5"
            onClick={() => setLayout(ASPECT_VER)}
            data-testid="mb-combine-vertical"
          >
            <Rows className="h-4 w-4" /> Stacked
          </Button>
          <div className="flex-1" />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Combined board name"
            className="text-xs px-2 py-1.5 rounded border border-input bg-white max-w-[200px]"
            data-testid="mb-combine-name"
          />
        </div>

        <div className="flex-1 overflow-auto p-5 bg-[#f1ede4]">
          {loadErr ? (
            <p className="text-sm text-[#a8324a] text-center py-10">Could not load both images.</p>
          ) : !imgs ? (
            <div className="flex items-center justify-center py-10 gap-2 text-[#5b6773]">
              <Loader2 className="h-4 w-4 animate-spin" /> Preparing preview…
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <canvas
                ref={previewCanvasRef}
                className="max-w-full max-h-[55vh] rounded shadow-[0_8px_32px_rgba(16,24,40,0.18)] bg-white"
                data-testid="mb-combine-canvas"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#e7e2d4]">
          <Button variant="outline" onClick={onClose} data-testid="mb-combine-cancel">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!imgs || saving}
            className="gap-1.5"
            data-testid="mb-combine-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Combine className="h-4 w-4" />}
            Save as new mood board
          </Button>
        </div>
      </div>
    </div>
  );
}

// end
